import React, { useEffect, useState } from 'react';
import { Table, Spin, message, Input, Button, Popconfirm, Form, Row, Col, Space, Modal } from 'antd';
import { ClearOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

const { confirm } = Modal;

export default function CheckInLogPage({ productId, productName }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(200);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [searchForm] = Form.useForm();
  const [searchQuery, setSearchQuery] = useState('');

  const loadData = () => {
    console.log('📊 載入簽到記錄', { page, size, search, sortField, sortOrder, productId });
    setLoading(true);
    const params = new URLSearchParams({
      page, size, search,
      ...(sortField && { sortField }),
      ...(sortOrder && { sortOrder }),
      ...(productId && { productId }) // 如果有活動ID，加入查詢參數
    });
    console.log('🔗 API URL:', `/api/checkin-logs?${params.toString()}`);
    fetch(`/api/checkin-logs?${params.toString()}`, { credentials: 'include' })
      .then(res => {
        console.log('📥 API 回應狀態:', res.status);
        if (!res.ok) throw new Error('取得簽到紀錄失敗');
        return res.json();
      })
      .then(({ data, total }) => {
        console.log('✅ 載入成功:', { 記錄數: data.length, 總計: total });
        setLogs(data);
        setTotal(total);
      })
      .catch(err => {
        console.error('❌ 載入失敗:', err);
        message.error(err.message);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, [page, size, search, sortField, sortOrder]);

  const handleSearch = value => { 
    setPage(1); 
    setSearch(value);
    setSearchQuery(value);
  };
  
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
  };
  
  const handleSearchSubmit = () => {
    console.log('🔍 點擊搜尋按鈕，搜尋關鍵字:', searchQuery);
    setPage(1);
    setSearch(searchQuery);
  };

  const handleClearSearch = () => {
    console.log('🧹 點擊清除按鈕');
    setSearchQuery('');
    setSearch('');
    setPage(1);
  };
  const handleExport = () => {
    console.log('📥 點擊匯出按鈕', { search, productId });
    const params = new URLSearchParams({ search });
    if (productId) {
      params.append('productId', productId);
    }
    console.log('📥 匯出 URL:', `/api/checkin-logs/export?${params.toString()}`);
    window.location.href = `/api/checkin-logs/export?${params.toString()}`;
  };
  const handleDelete = async (id) => {
    try {
      console.log('🗑️ 準備刪除簽到記錄 ID:', id);
      const res = await fetch(`/api/checkin-logs/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await res.json();
      console.log('🔍 刪除回應:', { status: res.status, data });

      if (!res.ok) {
        throw new Error(data.error || '刪除失敗');
      }

      message.success('刪除成功');
      loadData();
    } catch (err) {
      console.error('❌ 刪除失敗:', err);
      message.error('刪除失敗: ' + err.message);
    }
  };
  const handleTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current);
    setSize(pagination.pageSize);
    if (sorter.field) {
      setSortField(sorter.field);
      setSortOrder(sorter.order);
    } else {
      setSortField(null);
      setSortOrder(null);
    }
  };

  const columns = [
    { title: '序號', key: 'index', width: 60, render: (_, __, index) => (page - 1) * size + index + 1 },
    { title: '簽到時間', dataIndex: 'createdAt', key: 'createdAt', sorter: true, render: v => new Date(v).toLocaleString() },
    { title: 'LINE 名稱', dataIndex: 'lineName', key: 'lineName', sorter: true },
    { title: '社團', dataIndex: 'community', key: 'community', sorter: true },
    { title: '社別', dataIndex: 'department', key: 'department', sorter: true },
    { title: '社職', dataIndex: 'position', key: 'position', sorter: true },
    { title: '姓名', dataIndex: 'name', key: 'name', sorter: true },
    { title: '手機', dataIndex: 'mobile', key: 'mobile', sorter: true },
    { title: '簽到人', dataIndex: 'checkedBy', key: 'checkedBy', sorter: true },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => {
        console.log('🔍 渲染操作按鈕，記錄 ID:', record.id);
        return (
          <Button
            type="link"
            danger
            onClick={() => {
              console.log('🖱️ 點擊刪除按鈕，ID:', record.id);
              confirm({
                title: '確定刪除此簽到記錄嗎?',
                icon: <ExclamationCircleOutlined />,
                content: `LINE 名稱: ${record.lineName}`,
                okText: '確定刪除',
                okType: 'danger',
                cancelText: '取消',
                onOk() {
                  console.log('✅ 用戶確認刪除，ID:', record.id);
                  return handleDelete(record.id);
                },
                onCancel() {
                  console.log('❌ 用戶取消刪除');
                }
              });
            }}
          >
            刪除
          </Button>
        );
      }
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ fontWeight: 700, marginBottom: 16 }}>
        {productName ? `${productName} - 簽到紀錄` : '簽到紀錄管理'} (總計: {total} 人)
      </h2>
      {/* 搜尋區域 */}
      <Form form={searchForm} layout="vertical" style={{ marginBottom: 16, padding: 16, border: '1px solid #d9d9d9', borderRadius: 6, backgroundColor: '#fafafa' }}>
        <Row gutter={16}>
          <Col xs={24} sm={18} md={20} lg={20}>
            <Form.Item label="搜尋簽到資料" style={{ marginBottom: 0 }}>
              <Input 
                placeholder="搜尋 LINE 名稱、活動名稱、社團、社別、社職、姓名、手機、簽到人..." 
                allowClear 
                value={searchQuery}
                onChange={handleSearchChange}
                onPressEnter={handleSearchSubmit}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={6} md={4} lg={4}>
            <Form.Item label=" " style={{ marginBottom: 0 }}>
              <Space>
                <Button
                  icon={<ClearOutlined />}
                  onClick={() => {
                    console.log('🖱️ 點擊「清除」按鈕');
                    handleClearSearch();
                  }}
                >
                  清除
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    console.log('🖱️ 點擊「搜尋」按鈕');
                    handleSearchSubmit();
                  }}
                >
                  搜尋
                </Button>
                <Button
                  onClick={() => {
                    console.log('🖱️ 點擊「匯出 Excel」按鈕');
                    handleExport();
                  }}
                >
                  匯出 Excel
                </Button>
              </Space>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      {loading ? <Spin /> : (
        <Table
          rowKey="id"
          columns={columns}
          dataSource={logs}
          pagination={{
            current: page,
            pageSize: size,
            total: total,
            showSizeChanger: true,
            onChange: (p, s) => { setPage(p); setSize(s); }
          }}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
        />
      )}
    </div>
  );
}
